import { useEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * Click-to-edit text, as the prototype does it for collection titles (28px
 * h1), workspace names (26px), group names (16px h2) and group descriptions
 * (13.5px textarea). Enter commits titles, Cmd/Ctrl+Enter commits
 * descriptions, Esc cancels, blur commits. The editor draws a 1px #d4d4d2
 * box with negative margins so the text does not move.
 *
 * `onCommit('')` is called for an empty commit; the caller decides whether
 * that deletes a brand-new item (the prototype's isNew) or is ignored.
 */
export function InlineTitle({
  value,
  editing,
  onStart,
  onCommit,
  onCancel,
  size,
  placeholder,
  title = 'Click to rename',
  minWidth,
  hoverBg = '#f6f6f4',
  as = 'h1',
  readOnly = false,
  style,
}: {
  value: string;
  editing: boolean;
  onStart: () => void;
  onCommit: (v: string) => void;
  onCancel: () => void;
  size: 16 | 26 | 28;
  placeholder?: string;
  title?: string;
  minWidth?: number;
  hoverBg?: string;
  as?: 'h1' | 'h2';
  /**
   * Somebody who may read this name and not change it.
   *
   * A PROP rather than a no-op `onStart`, which is what callers reached for
   * first and which leaves every affordance in place: the text still says
   * "Click to rename", still lights up under the cursor and still shows a text
   * caret. It invites the click and then swallows it, which reads as the page
   * being broken rather than as the person lacking permission.
   */
  readOnly?: boolean;
  style?: CSSProperties;
}) {
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (editing) {
      setDraft(value);
      requestAnimationFrame(() => {
        ref.current?.focus();
        ref.current?.select();
      });
    }
  }, [editing, value]);
  const big = size !== 16;
  if (editing && !readOnly) {
    return (
      <input
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onCommit(draft.trim());
          }
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={() => onCommit(draft.trim())}
        placeholder={placeholder}
        style={{
          margin: big ? '0 -8px' : '0 -7px',
          padding: big ? '2px 7px' : '1px 6px',
          fontSize: size,
          fontWeight: 600,
          letterSpacing: big ? '-.02em' : undefined,
          fontFamily: 'inherit',
          color: '#0f0f10',
          border: '1px solid #d4d4d2',
          borderRadius: big ? 6 : 5,
          background: '#fff',
          outline: 'none',
          minWidth: minWidth ?? (big ? 320 : 260),
          ...style,
        }}
      />
    );
  }
  const Tag = as;
  return (
    <Tag
      title={readOnly ? undefined : title}
      onClick={readOnly ? undefined : (e) => {
        e.stopPropagation();
        onStart();
      }}
      className="tr-ellipsis"
      style={{
        margin: big ? '0 -8px' : '0 -7px',
        padding: big ? '2px 7px' : '1px 6px',
        borderRadius: big ? 6 : 5,
        fontSize: size,
        fontWeight: 600,
        letterSpacing: big ? '-.02em' : undefined,
        cursor: readOnly ? 'default' : 'text',
        transition: 'background .12s',
        flex: '0 1 auto',
        minWidth: 0,
        lineHeight: size === 28 ? 1.2 : undefined,
        ...style,
      }}
      onMouseEnter={readOnly ? undefined : (e) => {
        (e.currentTarget as HTMLElement).style.background = hoverBg;
      }}
      onMouseLeave={readOnly ? undefined : (e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      {value}
    </Tag>
  );
}

/** The group description: 13.5px paragraph, click to edit in a 2-row textarea, Cmd+Enter commits. */
export function InlineText({
  value,
  editing,
  onStart,
  onCommit,
  onCancel,
  placeholder = 'Add a description',
  readOnly = false,
}: {
  value: string;
  editing: boolean;
  onStart: () => void;
  onCommit: (v: string) => void;
  onCancel: () => void;
  placeholder?: string;
  /** Read it, do not change it — see InlineTitle's note on why this is a prop. */
  readOnly?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (editing) {
      setDraft(value);
      requestAnimationFrame(() => ref.current?.focus());
    }
  }, [editing, value]);
  if (editing && !readOnly) {
    return (
      <textarea
        ref={ref}
        value={draft}
        rows={2}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onCommit(draft.trim());
          }
          if (e.key === 'Escape') onCancel();
        }}
        onBlur={() => onCommit(draft.trim())}
        style={{
          margin: '0 -7px',
          padding: '3px 6px',
          fontSize: 13.5,
          lineHeight: 1.5,
          fontFamily: 'inherit',
          color: '#3f3f44',
          maxWidth: 720,
          width: '100%',
          boxSizing: 'border-box',
          border: '1px solid #d4d4d2',
          borderRadius: 5,
          background: '#fff',
          outline: 'none',
          resize: 'vertical',
        }}
      />
    );
  }
  // Nothing to say and no way to say it: a viewer looking at a group with no
  // description gets the placeholder "Add a description" as a promise the page
  // cannot keep, so the paragraph is simply not drawn.
  if (readOnly && !value) return null;
  return (
    <p
      title={readOnly ? undefined : 'Click to edit description'}
      className={readOnly ? undefined : 'tr-hover-white'}
      onClick={readOnly ? undefined : (e) => {
        e.stopPropagation();
        onStart();
      }}
      style={{ margin: '0 -7px', padding: '3px 6px', borderRadius: 5, fontSize: 13.5, lineHeight: 1.5, color: value ? '#3f3f44' : '#a0a0a4', maxWidth: 734, cursor: readOnly ? 'default' : 'text', transition: 'background .12s' }}
    >
      {value || placeholder}
    </p>
  );
}
